import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PopoverV2 } from '../PopoverV2';
import { Command, CommandItem, CommandGroup } from '../Command';
import { InputV2 } from '../InputV2';
import { ButtonV2 } from '../ButtonV2';
import { DrawerV2 } from '../DrawerV2';
import { DialogV2 } from '../DialogV2';
import { Box } from '../Box';
import { TextV2 } from '../TextV2';
import { useDisclosure } from '../../common/hooks/useDisclosure';
import { useId } from '../../common/hooks/useId';
import { createDisplayName } from '../../common/utils';
import { useFormControl } from '../FormControl/FormControl.provider';
import { ChevronDown, Check } from '../../icons';
import { useTimeout } from '@shared/hooks/useTimeout';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import type { ComboboxProps, ComboboxOption, ComboboxOptionGroup } from './Combobox.types';
import { useTranslation } from '@shared/core';
import {
  dialogContentOverrides,
  drawerContentOverrides,
  buttonModeDesktopCommandOverrides,
  buttonModeMobileCommandOverrides,
  buttonModeNoSearchRootPaddingTop,
} from './Combobox.styles.internal';
import { pxToRem } from '@withjoy/joykit/theme';

const DISABLE_FLIP_POPPER_OPTIONS = { disableFlip: true };

// ============================================================================
// Type guards
// ============================================================================

function isOptionGroup(item: ComboboxOption | ComboboxOptionGroup): item is ComboboxOptionGroup {
  return 'options' in item && Array.isArray(item.options);
}

function isCommandItem(
  element: React.ReactElement,
): element is React.ReactElement<{ value?: string; keywords?: string[]; disabled?: boolean; onSelect?: (value: string) => void; children?: React.ReactNode }> {
  if (element.type === CommandItem) return true;
  const type = element.type as { displayName?: string };
  return typeof element.type === 'function' && type.displayName === CommandItem.displayName;
}

function isCommandGroup(element: React.ReactElement): element is React.ReactElement<{ children?: React.ReactNode }> {
  if (element.type === CommandGroup) return true;
  const type = element.type as { displayName?: string };
  return typeof element.type === 'function' && type.displayName === CommandGroup.displayName;
}

function hasChildren(props: unknown): props is { children: React.ReactNode } {
  return !!(props && typeof props === 'object' && 'children' in props && props.children);
}

// ============================================================================
// Component
// ============================================================================

/**
 * Combobox is a searchable select component that combines PopoverV2, Command, and InputV2.
 * Supports both simple options mode and advanced children composition mode.
 *
 * ## Usage Modes
 *
 * **Simple Options Mode:**
 * ```tsx
 * <Combobox
 *   options={[
 *     { value: 'next', label: 'Next.js' },
 *     { value: 'remix', label: 'Remix' },
 *   ]}
 *   value={value}
 *   onValueChange={setValue}
 * />
 * ```
 *
 * **Children Composition Mode:**
 * ```tsx
 * <Combobox value={value} onValueChange={setValue}>
 *   <Command.Group heading="Actions">
 *     <Command.Item value="calendar">
 *       <Flex justifyContent="space-between" width="100%">
 *         <span>Calendar</span>
 *         <span>⌘K</span>
 *       </Flex>
 *     </Command.Item>
 *   </Command.Group>
 * </Combobox>
 * ```
 *
 * **Button Mode with Dialog/Drawer:**
 * ```tsx
 * <Combobox
 *   mode="button"
 *   dialogTitle="Choose Currency"
 *   value={value}
 *   onValueChange={setValue}
 *   buttonProps={{ intent: 'neutral', shape: 'rounded' }}
 * >
 *   <Command.Item value="USD">
 *     <Flex flexDirection="column">
 *       <TextV2>US Dollar</TextV2>
 *       <TextV2 color="mono7">USD - $</TextV2>
 *     </Flex>
 *   </Command.Item>
 * </Combobox>
 * ```
 */
export const Combobox: React.FC<ComboboxProps> = ({
  options,
  children,
  value: valueProp,
  defaultValue,
  onValueChange,
  placeholder,
  emptyMessage,
  searchPlaceholder,
  displayValue: displayValueProp,
  startElement,
  mode = 'input',
  buttonProps,
  buttonModeMobileIcon,
  multiple = false,
  disabled,
  name,
  onBlur,
  onOpenChange,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-errormessage': ariaErrorMessage,
  searchable = true,
  searchResultsDisplay = 'grouped',
  placement = 'bottomStart',
  disableFlip = false,
  dialogTitle,
  saveLabel,
}) => {
  const { tq } = useTranslation('combobox');
  const isMobile = useIsMobileScreen();

  const resolvedPlaceholder = placeholder ?? tq`Select...`;
  const resolvedEmptyMessage = emptyMessage ?? tq`No results found`;
  const resolvedSearchPlaceholder = searchPlaceholder ?? tq`Search...`;
  const resolvedSaveLabel = saveLabel ?? tq`Save`;

  // `disabled` is deliberately left un-defaulted: `useFormControl` gives precedence to any prop
  // the child actually passed, so defaulting it to `false` here would mean a surrounding
  // `<FormControl isDisabled>` could never disable this field.
  const formControlProps = useFormControl({ isDisabled: disabled });
  const resolvedDisabled = formControlProps.isDisabled ?? false;
  const resolvedError = formControlProps.isInvalid ?? false;

  const { isOpen, onOpen, onClose } = useDisclosure({
    onOpen: () => onOpenChange?.(true),
    onClose: () => onOpenChange?.(false),
  });
  const listId = useId(undefined, 'combobox-list');
  const triggerRef = useRef<HTMLDivElement>(null);
  const commandRootRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);
  const { set: setFocusTimeout, clear: clearFocusTimeout } = useTimeout();

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [isOpen]);

  // Focus management (input mode only — dialog/drawer handle their own)
  useEffect(() => {
    if (mode === 'input' && isOpen && commandRootRef.current) {
      if (searchable) {
        const inputElement = commandRootRef.current.querySelector('input');
        if (inputElement) {
          setFocusTimeout(() => (inputElement as HTMLInputElement).focus({ preventScroll: true }), 100);
        }
      } else {
        const commandRoot = commandRootRef.current.querySelector('[role="application"]') as HTMLElement;
        if (commandRoot) {
          setFocusTimeout(() => commandRoot.focus({ preventScroll: true }), 100);
        }
      }
    }
    return () => clearFocusTimeout();
  }, [mode, isOpen, searchable, setFocusTimeout, clearFocusTimeout]);

  // ============================================================================
  // Value state
  // ============================================================================

  const [internalValue, setInternalValue] = useState<string | ReadonlyArray<string>>(defaultValue ?? (multiple ? [] : ''));
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;
  const valueArray: ReadonlyArray<string> = Array.isArray(value) ? value : value ? [value] : [];

  // Mobile deferred selection (button mode only)
  const [pendingValue, setPendingValue] = useState<string | ReadonlyArray<string>>(multiple ? [] : '');
  // pendingValue is seeded from `value`, so it can't tell an untouched Save from a real pick.
  const [hasPendingSelection, setHasPendingSelection] = useState(false);

  useEffect(() => {
    if (isOpen && mode === 'button' && isMobile) {
      setPendingValue(value ?? (multiple ? [] : ''));
      setHasPendingSelection(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const pendingValueArray: ReadonlyArray<string> = Array.isArray(pendingValue) ? pendingValue : pendingValue ? [pendingValue] : [];

  // ============================================================================
  // Options & display value
  // ============================================================================

  const flatOptions: ReadonlyArray<ComboboxOption> = options ? options.flatMap(item => (isOptionGroup(item) ? item.options : [item])) : [];

  const computedDisplayValue = (): string => {
    if (valueArray.length === 0) return '';
    if (multiple) {
      return tq.plural(valueArray.length, {
        one: tq`${valueArray.length} selected`,
        other: tq`${valueArray.length} selected`,
      });
    }
    if (flatOptions.length > 0) {
      const selectedOption = flatOptions.find(opt => opt.value === valueArray[0]);
      return selectedOption?.label ?? valueArray[0];
    }
    return valueArray[0];
  };

  const displayValue = displayValueProp ?? computedDisplayValue();

  // ============================================================================
  // Selection handlers
  // ============================================================================

  // Immediate commit (input mode + desktop button mode)
  const handleSelect = useCallback(
    (selectedValue: string) => {
      if (multiple) {
        const currentArray = valueArray;
        const isSelected = currentArray.includes(selectedValue);
        const newValue = isSelected ? currentArray.filter(v => v !== selectedValue) : [...currentArray, selectedValue];
        if (!isControlled) setInternalValue(newValue);
        (onValueChange as ((value: ReadonlyArray<string>) => void) | undefined)?.(newValue);
      } else {
        if (!isControlled) setInternalValue(selectedValue);
        (onValueChange as ((value: string) => void) | undefined)?.(selectedValue);
        onClose();
      }
    },
    [valueArray, multiple, isControlled, onValueChange, onClose],
  );

  // Deferred (mobile button mode — pending until Save)
  const handlePendingSelect = useEventCallback((selectedValue: string) => {
    setHasPendingSelection(true);
    if (multiple) {
      const isSelected = pendingValueArray.includes(selectedValue);
      setPendingValue(isSelected ? pendingValueArray.filter(v => v !== selectedValue) : [...pendingValueArray, selectedValue]);
    } else {
      setPendingValue(selectedValue);
    }
  });

  const handleMobileSave = useEventCallback(() => {
    if (!hasPendingSelection) {
      onClose();
      return;
    }
    if (multiple) {
      if (!isControlled) setInternalValue(pendingValue);
      (onValueChange as ((value: ReadonlyArray<string>) => void) | undefined)?.(pendingValueArray);
    } else {
      const pendingStr = pendingValue as string;
      if (pendingStr) {
        if (!isControlled) setInternalValue(pendingStr);
        (onValueChange as ((value: string) => void) | undefined)?.(pendingStr);
      }
    }
    onClose();
  });

  const handleMobileClose = useEventCallback(() => {
    onClose();
  });

  // ============================================================================
  // Render helpers for options prop (parameterized with select handler)
  // ============================================================================

  // The Item overrides Command receives for the active mode — re-applied per item when a
  // stacked row needs its own selected fill (a local override replaces this one wholesale).
  const commandOverrides = useMemo(() => {
    if (mode !== 'button') {
      return undefined;
    }
    const base = isMobile ? buttonModeMobileCommandOverrides : buttonModeDesktopCommandOverrides;
    if (searchable) {
      // The search input owns the tab stop and holds the list clear of the close button, so the
      // root keeps Command's default tabIndex={-1} and needs no extra padding.
      return base;
    }
    return {
      ...base,
      Root: {
        ...base.Root,
        props: {
          ...base.Root?.props,
          // Reachable by Tab so keyboard users still get the arrow-key/Enter handler that the
          // root owns — the dialog's close button and the drawer's Save sit outside it.
          tabIndex: 0,
          // Only the desktop dialog needs this: the mobile drawer has a header the close button
          // lives in, so nothing floats over the list there.
          ...(!isMobile && { paddingTop: buttonModeNoSearchRootPaddingTop }),
        },
      },
    };
  }, [mode, isMobile, searchable]);

  const renderItem = useCallback(
    (option: ComboboxOption, selectHandler: (value: string) => void, activeValues: ReadonlyArray<string>) => {
      const isSelected = activeValues.includes(option.value);
      // Command's row highlight follows the hovered/keyboard-active option, not the committed
      // value, so a stacked row (which has no check) needs its own persistent fill — otherwise
      // moving the pointer elsewhere leaves the current selection unmarked. It must differ from
      // the highlight's mono2, or the committed and hovered rows become indistinguishable.
      // Merge the active mode's Item overrides back in, since a local override replaces the
      // Command-level one and would otherwise drop the button-mode grid sizing.
      const stackedSelectedOverrides =
        option.description && isSelected && !multiple ? { Item: { props: { ...(commandOverrides?.Item?.props ?? {}), backgroundColor: 'mono3' } } } : undefined;
      return (
        <Command.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          keywords={option.keywords ? [option.label, ...option.keywords] : [option.label]}
          onSelect={selectHandler}
          overrides={stackedSelectedOverrides}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            width="100%"
            __css={isSelected && !multiple ? { fontWeight: 500 } : {}}
          >
            {option.description ? (
              <Box
                display="flex"
                flexDirection="column"
              >
                {/* Input mode: body2 (16px) is what an undescribed row inherits from
                    commandItemStyles, so the label keeps one size either way, and the description
                    takes label2 (13px). The variant scales ascend — body1 is 15px, so the old
                    body1/body2 pairing rendered the description larger than its own label.
                    Button mode was already ordered correctly (button1 15px over label1 12px). */}
                <TextV2 typographyVariant={mode === 'button' ? 'button1' : 'body2'}>{option.label}</TextV2>
                <TextV2
                  typographyVariant={mode === 'button' ? 'label1' : 'label2'}
                  color={mode === 'button' ? 'mono10' : 'mono7'}
                  marginTop={mode === 'button' ? { _: 0, sm2: 1 } : 0}
                >
                  {option.description}
                </TextV2>
              </Box>
            ) : (
              option.label
            )}
            {/* Stacked rows read their selected state from the row highlight, so the check is
                redundant there — except in multi-select, where it's the only indicator. */}
            {isSelected && (multiple || !option.description) && (
              <Box
                as="span"
                marginLeft="auto"
              >
                <Check size={16} />
              </Box>
            )}
          </Box>
        </Command.Item>
      );
    },
    [multiple, mode, commandOverrides],
  );

  const renderGroup = useCallback(
    (group: ComboboxOptionGroup, index: number, selectHandler: (value: string) => void, activeValues: ReadonlyArray<string>) => (
      <Command.Group
        key={`group-${index}`}
        heading={group.label}
      >
        {group.options.map(option => renderItem(option, selectHandler, activeValues))}
      </Command.Group>
    ),
    [renderItem],
  );

  const renderOptions = useCallback(
    (opts: ReadonlyArray<ComboboxOption | ComboboxOptionGroup>, selectHandler: (value: string) => void, activeValues: ReadonlyArray<string>) => {
      if (searchResultsDisplay === 'flat') {
        const allOptions: ComboboxOption[] = [];
        opts.forEach(item => {
          if (isOptionGroup(item)) allOptions.push(...item.options);
          else allOptions.push(item);
        });
        return allOptions.map(option => renderItem(option, selectHandler, activeValues));
      }
      return opts.map((item, index) => {
        if (isOptionGroup(item)) return renderGroup(item, index, selectHandler, activeValues);
        return renderItem(item, selectHandler, activeValues);
      });
    },
    [searchResultsDisplay, renderItem, renderGroup],
  );

  // ============================================================================
  // Enhance children to inject onSelect handler
  // ============================================================================

  const enhanceChildrenWithHandler = useCallback((childNodes: React.ReactNode, selectHandler: (value: string) => void): React.ReactNode => {
    const enhance = (nodes: React.ReactNode): React.ReactNode => {
      return React.Children.map(nodes, child => {
        if (!React.isValidElement(child)) return child;

        if (isCommandItem(child)) {
          const originalOnSelect = child.props.onSelect;
          return React.cloneElement(child, {
            ...child.props,
            onSelect: (selectedValue: string) => {
              originalOnSelect?.(selectedValue);
              selectHandler(selectedValue);
            },
          } as Record<string, unknown>);
        }

        if (isCommandGroup(child) && child.props.children) {
          return React.cloneElement(child, {
            children: enhance(child.props.children),
          });
        }

        if (hasChildren(child.props)) {
          return React.cloneElement(child, {
            children: enhance(child.props.children),
          } as Partial<typeof child.props>);
        }

        return child;
      });
    };
    return enhance(childNodes);
  }, []);

  // Pre-built enhanced children for each mode
  const enhancedDesktopChildren = useMemo(() => (children ? enhanceChildrenWithHandler(children, handleSelect) : null), [children, enhanceChildrenWithHandler, handleSelect]);

  const enhancedMobileChildren = useMemo(
    () => (children ? enhanceChildrenWithHandler(children, handlePendingSelect) : null),
    [children, enhanceChildrenWithHandler, handlePendingSelect],
  );

  // ============================================================================
  // Shared UI pieces
  // ============================================================================

  const chevronIcon = (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      style={{
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s ease',
      }}
    >
      <ChevronDown size={16} />
    </Box>
  );

  // ============================================================================
  // Button mode rendering
  // ============================================================================

  if (mode === 'button') {
    const trigger =
      isMobile && buttonModeMobileIcon ? (
        <Box
          as="button"
          name={name}
          onClick={isOpen ? onClose : onOpen}
          onBlur={onBlur}
          disabled={resolvedDisabled}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-controls={listId}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-errormessage={ariaErrorMessage}
          __css={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
        >
          {buttonModeMobileIcon}
        </Box>
      ) : (
        <ButtonV2
          {...buttonProps}
          name={name}
          onClick={isOpen ? onClose : onOpen}
          onBlur={onBlur}
          disabled={resolvedDisabled}
          endIcon={buttonProps?.endIcon ?? chevronIcon}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-controls={listId}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-errormessage={ariaErrorMessage}
        >
          {displayValue || resolvedPlaceholder}
        </ButtonV2>
      );

    return (
      <Box>
        {trigger}

        {isMobile ? (
          <DrawerV2
            isOpen={isOpen}
            onClose={handleMobileClose}
            anchor="bottom"
            size="auto"
            overrides={{
              Content: { props: drawerContentOverrides },
            }}
          >
            {dialogTitle && (
              <DrawerV2.Header
                paddingY={7}
                paddingX={pxToRem(40)}
              >
                <TextV2
                  textAlign="left"
                  typographyVariant="hed4"
                >
                  {dialogTitle}
                </TextV2>
                <DrawerV2.CloseButton
                  height={pxToRem(40)}
                  width={pxToRem(40)}
                  minWidth={pxToRem(40)}
                  borderRadius="50%"
                  border="1px solid"
                  borderColor="mono5"
                  paddingX={0}
                  paddingY={0}
                  marginTop={pxToRem(16)}
                  marginRight={pxToRem(24)}
                />
              </DrawerV2.Header>
            )}

            <DrawerV2.Body
              flex={1}
              overflow="hidden"
              padding={0}
              display="flex"
              flexDirection="column"
            >
              <Command
                overrides={commandOverrides}
                shouldFilter={searchable}
                value={pendingValueArray[0]}
              >
                {searchable && <Command.Input placeholder={resolvedSearchPlaceholder} />}
                <Command.List>
                  <Command.Empty>{resolvedEmptyMessage}</Command.Empty>
                  {options && renderOptions(options, handlePendingSelect, pendingValueArray)}
                  {enhancedMobileChildren}
                </Command.List>
              </Command>
            </DrawerV2.Body>

            <DrawerV2.Footer
              paddingX={pxToRem(40)}
              paddingY={pxToRem(24)}
              borderTop="1px solid"
              borderColor="mono3"
            >
              <ButtonV2
                onClick={handleMobileSave}
                fullWidth
                intent="neutral"
                height={pxToRem(48)}
                size="md"
                shape="rounded"
              >
                {resolvedSaveLabel}
              </ButtonV2>
            </DrawerV2.Footer>
          </DrawerV2>
        ) : (
          <DialogV2
            isOpen={isOpen}
            onClose={onClose}
            scrollBehavior="inside"
            overrides={{
              Content: { props: dialogContentOverrides },
            }}
          >
            <DialogV2.CloseButton
              height={pxToRem(40)}
              width={pxToRem(40)}
              minWidth={pxToRem(40)}
              borderRadius="50%"
              border="1px solid"
              borderColor="mono5"
              paddingX={0}
              paddingY={0}
              marginTop={pxToRem(12)}
              marginRight={pxToRem(20)}
            />

            <DialogV2.Body
              padding={0}
              display="flex"
              flexDirection="column"
              flex={1}
              overflow="hidden"
            >
              <Command
                overrides={commandOverrides}
                shouldFilter={searchable}
                value={valueArray[0]}
              >
                {searchable && <Command.Input placeholder={resolvedSearchPlaceholder} />}
                <Command.List>
                  <Command.Empty>{resolvedEmptyMessage}</Command.Empty>
                  {options && renderOptions(options, handleSelect, valueArray)}
                  {enhancedDesktopChildren}
                </Command.List>
              </Command>
            </DialogV2.Body>
          </DialogV2>
        )}
      </Box>
    );
  }

  // ============================================================================
  // Input mode rendering (default)
  // ============================================================================

  const inputCommandChildren = (
    <>
      {searchable && <Command.Input placeholder={resolvedSearchPlaceholder} />}
      <Command.List>
        <Command.Empty>{resolvedEmptyMessage}</Command.Empty>
        {options && renderOptions(options, handleSelect, valueArray)}
        {enhancedDesktopChildren}
      </Command.List>
    </>
  );

  return (
    <Box>
      <PopoverV2
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        triggerType="click"
        placement={placement}
        popperOptions={disableFlip ? DISABLE_FLIP_POPPER_OPTIONS : undefined}
      >
        <PopoverV2.Trigger>
          <Box
            ref={triggerRef}
            tabIndex={-1}
            onMouseDown={e => {
              // Prevent trigger from taking focus on click so the page doesn't scroll to it
              e.preventDefault();
            }}
          >
            <InputV2
              isReadOnly
              // The menu is open but focus has moved into its search box, and this trigger refuses
              // focus on mousedown -- so the ring has to be driven by open state, not DOM focus.
              isActive={isOpen}
              name={name}
              value={displayValue}
              placeholder={resolvedPlaceholder}
              isDisabled={resolvedDisabled}
              isInvalid={resolvedError}
              startElement={startElement}
              endElement={chevronIcon}
              onBlur={onBlur}
              aria-expanded={isOpen}
              aria-controls={listId}
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              aria-errormessage={ariaErrorMessage}
              role="combobox"
              // The display value is read-only and can be a long comma-joined list (e.g. selected tags);
              // ellipsize it instead of clipping mid-character so overflow reads as "…".
              overrides={{ Input: { props: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } } }}
            />
          </Box>
        </PopoverV2.Trigger>

        <PopoverV2.Content>
          <Box
            width={triggerWidth ? `${triggerWidth}px` : '100%'}
            minWidth="200px"
          >
            <Box
              ref={commandRootRef}
              __css={{
                outline: 'none',
                '&:focus': { outline: 'none' },
                '& *:focus': { outline: 'none' },
              }}
            >
              <Command
                shouldFilter={searchable}
                // Input mode commits immediately on select, so highlight the committed value.
                value={valueArray[0]}
                overrides={{ Root: { props: { outline: 'none' } } }}
              >
                {inputCommandChildren}
              </Command>
            </Box>
          </Box>
        </PopoverV2.Content>
      </PopoverV2>
    </Box>
  );
};

Combobox.displayName = createDisplayName('Combobox');
